import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Ac2AccessGroupModel } from "src/api/access/ac-group-types";
import { getAccessGroupListAsync } from "src/api/access/productac-api";
import { AccessPageQueryParams } from "src/api/access/productac-types";
import { useApiOperation } from "src/api/hooks";
import { Order, PageMeta } from "src/api/public-types";
import { BaseButton, BaseInputWithSearch, BaseSelect } from "src/components";
import { BaseTable } from "src/components/BaseTable";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { globalPartnerState } from "src/recoil/partners/atom";
import { PaPartner } from "src/types/partner";
import { accessGroupSearchFilters, accessGroupSearchType, floorAccessSearchFilterKeys } from "../const";
import { accessGroupColumns, floorAccessColumnAccessors } from "./AccessGroupColumns";
import SearchResetButton from "src/components/SearchResetButton";

const AccessGroupList = () => {
  const location = useLocation();
  const partner = useRecoilValue(globalPartnerState);

  // 파트너 feature > CTRLROOM_ACCESS_GROUP_ACCESS_FLOORS 여부 판단 (엘리베이터 층수 컬럼/검색조건 노출 여부)
  const isFloorAccessFeatureActive = useMemo(() => {
    return partner?.features?.some((feature: PaPartner) => feature.name === "CTRLROOM_ACCESS_GROUP_ACCESS_FLOORS" && feature.active) || false;
  }, [partner]);

  const columns = useMemo(() => {
    return isFloorAccessFeatureActive
      ? accessGroupColumns
      : accessGroupColumns.filter((column) => !floorAccessColumnAccessors.includes(column.accessor));
  }, [isFloorAccessFeatureActive]);

  const searchFilters = useMemo(() => {
    return isFloorAccessFeatureActive
      ? accessGroupSearchFilters
      : accessGroupSearchFilters.filter((filter) => !floorAccessSearchFilterKeys.includes(filter.key));
  }, [isFloorAccessFeatureActive]);

  const { isAuthority } = usePartnerAuthority();
  const navigate = useNavigate();
  const [pageMeta, setPageMeta] = useState<PageMeta>();

  const queryParams = useMemo(() => {
    const query: AccessPageQueryParams = qs.parse(location.search, { allowDots: true, ignoreQueryPrefix: true });
    if (!query.page) {
      query.page = 0;
    }
    if (!query.size) {
      query.size = 20;
    }
    if (!query?.sort) {
      query.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }

    return query;
  }, [location]);

  // 액세스그룹
  const [accessGroups, setAccessGroups] = useState<Ac2AccessGroupModel[]>([]);

  const [searchParams, setSearchParams] = useState<AccessPageQueryParams>(queryParams);

  const { executeAsync: getAccessList } = useApiOperation(getAccessGroupListAsync);

  const getAccessGroupsList = async (passParams?: AccessPageQueryParams) => {
    // 조회
    const { data } = await getAccessList(passParams);
    setAccessGroups(data?.data.content || null);
    setPageMeta(data?.meta.pageMeta);
  };

  useEffect(() => {
    setSearchParams(queryParams);
    getAccessGroupsList(queryParams);
  }, [location]);

  // URL 쿼리 파라미터 업데이트 함수
  const updateQueryParams = (newParams: Partial<AccessPageQueryParams>) => {
    const currentValues = searchParams;
    const updatedParams = { ...currentValues, ...newParams };

    // 빈 검색어는 파라미터에서 제거
    if (updatedParams.searchValue === "") {
      delete updatedParams.searchValue;
    }
    console.log("newParams", newParams);
    const queryString = qs.stringify(updatedParams, { allowDots: true });
    navigate(location.pathname + (queryString ? `?${decodeURIComponent(queryString)}` : ""));
  };

  // 검색 실행 함수
  const handleSearch = () => {
    const { searchType, searchValue } = searchParams;
    updateQueryParams({ ...searchParams, page: 0, searchType, searchValue });
  };

  // 검색 초기화 함수
  const clearSearch = () => {
    setSearchParams({ ...searchParams, page: 0, searchValue: "", searchType: "" });
  };

  // 셀렉트 필터 변경 함수 ("전체" 선택 시 쿼리 파라미터에서 제거)
  const handleFilterChange = (key: (typeof accessGroupSearchFilters)[number]["key"], value: string) => {
    updateQueryParams({ ...searchParams, [key]: value === "" ? undefined : value, page: 0 });
  };

  // 페이지 변경 함수
  const handleGoPage = (page: number) => {
    updateQueryParams({ ...searchParams, page });
  };

  // 페이지 크기 변경 함수
  const handleViewSize = (size: number) => {
    updateQueryParams({ ...searchParams, page: 0, size });
  };

  // 정렬 변경 함수
  const handleSort = (orders: Order[]) => {
    updateQueryParams({ ...searchParams, sort: { orders } });
  };

  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <BaseInputWithSearch
                  type="text"
                  className="mr10"
                  selectValue={searchParams.searchType}
                  inputValue={searchParams.searchValue}
                  stateOptions={accessGroupSearchType}
                  setStateValue={(value: string) => setSearchParams({ ...searchParams, searchType: value })}
                  onChange={(value: string) => setSearchParams({ ...searchParams, searchValue: value })}
                  onClearClick={clearSearch}
                  onKeyUp={() => handleSearch()}
                  onSearchClick={handleSearch}
                />
                {searchFilters.map((filter) => (
                  <div key={filter.key} className={`${filter.widthClassName ?? "minmax140"} mr10`}>
                    <BaseSelect
                      className="w-100"
                      placeholder={filter.placeholder}
                      stateOptions={filter.options}
                      value={String(queryParams[filter.key] ?? "")}
                      setStateValue={(value: string) => handleFilterChange(filter.key, value)}
                    />
                  </div>
                ))}
                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area">
            {isAuthority("w") && <BaseButton className="ml20" title="+ 출입그룹 등록" onClick={() => navigate(PagePath.accessGroup.form)} />}
          </div>
        </div>
        <BaseTable
          data={accessGroups}
          currentSize={Number(queryParams.size) || 20}
          sizeOption={handleViewSize}
          pageIndex={Number(queryParams?.page || 0)}
          totalElements={pageMeta?.totalElements}
          totalPages={pageMeta?.totalPages || 0}
          goPage={handleGoPage}
          orders={queryParams?.sort?.orders}
          setOrders={handleSort}
          disabledSortHeaders={columns.map((column) => column.accessor)}
          columns={columns}
        />
      </div>
    </div>
  );
};

export default AccessGroupList;
